import { useState } from 'react';
import { LoadingButton } from '@mui/lab';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { updatePrescriptionReviewReason } from 'store/thunks/patientThunks';
import { loadPrescriptionData } from 'store/thunks/workQueueThunks';
import { enqueueSnackbar } from 'notistack';
import useInfoDialog from 'common/hooks/useInfoDialog';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from '@mui/material';
import { capitalize, orderBy } from 'lodash';
import { NON_SELECTABLE_REVIEW_REASONS, PrescriptionReviewStatus } from 'enums/prescription.enum';
import PatientBanner from 'common/modules/PatientBanner';
import DrugDetailesTab from './drugDetailestab';

const ChangeReviewReasonDialog = ({
  open,
  onClose,
  patientId,
  firstName,
  lastName,
  dob,
  email,
  orderId,
  lineItem,
  prescriptionId,
  drugName,
  form,
  drugUnit,
  strength,
  gender,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [selectReviewReason, setSelectReviewReason] = useState('');
  const { InfoDialog, showInfoDialog } = useInfoDialog();

  const closeChangeReviewForReasonDialog = () => {
    onClose();
    setSelectReviewReason('');
  };

  const handleChangeReasonForReview = () => {
    showInfoDialog({
      title: 'Confirm Change Reason',
      message: `Are you sure you want to change the review reason to ${selectReviewReason
        .toLowerCase()
        .replace(/_/g, ' ')}?`,
      callback: () => {
        setLoading(true);
        dispatch(
          updatePrescriptionReviewReason({
            patientId,
            prescriptionUUID: prescriptionId,
            lineItemId: lineItem,
            reviewTarget: selectReviewReason,
            orderId,
          })
        )
          .then((result) => {
            if (result) {
              const message = `The reason for the review has been successfully updated`;
              enqueueSnackbar(message, { variant: 'success' });
              if (lineItem && prescriptionId && selectReviewReason) {
                dispatch(
                  loadPrescriptionData({
                    lineItemId: lineItem,
                    prescriptionId,
                    reviewType: selectReviewReason,
                  })
                );
              }
            }
          })
          .finally(() => {
            setSelectReviewReason('');
            setLoading(false);
            onClose();
          });
      },
      cancelable: true,
      isWarning: true,
      confirmButtonText: 'Yes',
    });
  };

  return (
    <>
      <InfoDialog />
      <Grid>
        <Dialog
          open={open}
          onClose={closeChangeReviewForReasonDialog}
          aria-describedby='changeReviewReason-header'
        >
          <DialogTitle sx={{ mt: '10px', mb: 0, pb: 0, fontWeight: 600 }}>
            Change Reason For Review
          </DialogTitle>
          <Box sx={{ m: '20px' }}>
            <PatientBanner
              mpi={patientId}
              firstName={firstName}
              lastName={lastName}
              dob={dob}
              email={email}
              gender={gender}
            />
          </Box>
          <DrugDetailesTab
            drugName={drugName}
            strength={strength}
            drugUnit={drugUnit}
            form={form}
          />
          <DialogContent sx={{ minWidth: 500 }}>
            <Grid container style={{ display: 'flex' }}>
              <Grid container justifyContent='flex-end' marginTop='10px' marginBottom='20px'>
                <TextField
                  select
                  size='small'
                  label='Select Reason'
                  value={selectReviewReason}
                  onChange={(e) => setSelectReviewReason(e.target.value)}
                  sx={{ minWidth: 180 }}
                  required
                >
                  {orderBy(
                    Object.entries(PrescriptionReviewStatus),
                    [([key]) => key.replace(/_/g, ' ')],
                    ['asc']
                  )
                    .filter(([, value]) => !NON_SELECTABLE_REVIEW_REASONS.includes(value))
                    .map(([key, value]) => (
                      <MenuItem key={key} value={value}>
                        {capitalize(key.replace(/_/g, ' ').toLowerCase())}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={closeChangeReviewForReasonDialog}
              color='secondary'
              size='medium'
              variant='outlined'
            >
              Cancel
            </Button>
            <LoadingButton
              loading={loading}
              variant='contained'
              onClick={handleChangeReasonForReview}
              disabled={!selectReviewReason?.length}
            >
              Confirm
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Grid>
    </>
  );
};

ChangeReviewReasonDialog.propTypes = {
  patientId: PropTypes.string.isRequired,
  orderId: PropTypes.string.isRequired,
  lineItem: PropTypes.string.isRequired,
  prescriptionId: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  dob: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  gender: PropTypes.string.isRequired,
  form: PropTypes.string,
  strength: PropTypes.string.isRequired,
  drugName: PropTypes.string.isRequired,
  drugUnit: PropTypes.string,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

ChangeReviewReasonDialog.defaultProps = {
  form: null,
  drugUnit: null,
};

export default ChangeReviewReasonDialog;
